import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { useSelector } from 'react-redux';

import { preferencesSelector, useUpdateUserPreferencesMutation } from '@/api/v1';
import { UpdateUserPreferencesRequest, UserPreferences } from '@/DTOs/userPreferences';

const defaultPreferences: UserPreferences = localStorage.getItem('preferences')
  ? JSON.parse(localStorage.getItem('preferences') as string)
  : {
      notifications: {
        transferStatus: {
          created: true,
          completed: true,
          failed: true,
          paymentSent: true,
        },
        depositStatus: {
          created: true,
          completed: true,
          failed: true,
        },
        paymentApproval: {
          awaitingApproval: true,
          approvalDecision: true,
        },
      },
      appearance: {
        theme: 'light',
      },
      timezone: 'America/Sao_Paulo',
      language: 'en',
    };

export const useUserPreferences = (): [
  UserPreferences,
  (value: UpdateUserPreferencesRequest) => Promise<void>,
  boolean,
] => {
  const { data, isLoading: fetching, isSuccess: fetched } = useSelector(preferencesSelector);

  const [preferences, setPreferences] = React.useState<UserPreferences>(defaultPreferences);
  const [update, mutation] = useUpdateUserPreferencesMutation();

  const updatePreferences = React.useCallback(
    async (patch: UpdateUserPreferencesRequest): Promise<void> => {
      await update(patch).unwrap();
    },
    [update],
  );

  React.useEffect((): void => {
    if (fetched && data) {
      setPreferences(data);
    }
  }, [data, fetched]);

  React.useEffect((): void => {
    if (mutation.status === QueryStatus.fulfilled) {
      // Save preferences locally
      localStorage.setItem('preferences', JSON.stringify(mutation.data));
      setPreferences(mutation.data);
    }
  }, [mutation.data, mutation.status]);

  return [preferences, updatePreferences, fetching || mutation.status === QueryStatus.pending];
};
